import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { journal as journalApi, accounts as accountsApi } from "../api/endpoints";
import type { JournalSource } from "../api/types";
import { AccountPicker } from "../components/AccountPicker";
import { HistoryStatsCard } from "../components/HistoryStatsCard";
import { EquityCurve, type EquitySeries } from "../components/EquityCurve";
import { JournalTable } from "../components/JournalTable";
import { SleevePerfTable } from "../components/SleevePerfTable";
import { EdgeVerificationCard } from "../components/EdgeVerificationCard";
import { useActiveLinkedAlgoIds } from "../hooks/useActiveLinkedAlgoIds";

const CURVE_CAP = 5000;

type RangePreset = "deploy" | "30d" | "7d";

function sinceFor(p: RangePreset): string | undefined {
  if (p === "deploy") return undefined;
  const days = p === "7d" ? 7 : 30;
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

export function AnalyticsDashboard() {
  const [preset, setPreset] = useState<RangePreset>("deploy");
  const [algoPreset, setAlgoPreset] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const since = sinceFor(preset);

  // ── Accounts for picker ─────────────────────────────────────────────
  const accountsQ = useQuery({
    queryKey: ["accounts"],
    queryFn: accountsApi.list,
    refetchInterval: 30_000,
  });
  const list = accountsQ.data ?? [];
  // Scope account id drives journal + edge-verification queries. When null, aggregate across all.

  // Active-link set for the edge verification panel (union across non-retired
  // accounts under ALL; the selected account's links otherwise).
  const { linkedAlgoIds } = useActiveLinkedAlgoIds(list, selectedId);

  // ── Journal queries (scoped to selected account) ──────────────────────
  const aggregatesQ = useQuery({
    queryKey: ["journal", "aggregates", since ?? "all", selectedId],
    queryFn: () => journalApi.aggregates({ since, account_id: selectedId ?? undefined }),
    refetchInterval: 30_000,
  });

  const curveQ = useQuery({
    queryKey: ["journal", "curve", since ?? "all", selectedId],
    queryFn: () => journalApi.list({ since, limit: CURVE_CAP, account_id: selectedId ?? undefined }),
    refetchInterval: 30_000,
  });

  const series = useMemo<EquitySeries[]>(() => {
    const rows = (curveQ.data ?? []).filter((r) => r.exit_ts != null && r.pnl_net != null);
    rows.sort((a, b) => (a.exit_ts! < b.exit_ts! ? -1 : 1));
    const bySource = new Map<JournalSource, { t: number; cum: number }[]>();
    const cum = new Map<JournalSource, number>();
    for (const r of rows) {
      const next = (cum.get(r.source) ?? 0) + (r.pnl_net ?? 0);
      cum.set(r.source, next);
      const arr = bySource.get(r.source) ?? [];
      arr.push({ t: new Date(r.exit_ts!).getTime(), cum: next });
      bySource.set(r.source, arr);
    }
    return [...bySource.entries()].map(([src, points]) => ({ source: src, points }));
  }, [curveQ.data]);

  const sleeveOptions = useMemo(() => {
    const s = new Set<string>();
    for (const a of aggregatesQ.data?.by_algo ?? []) s.add(a.algo_preset);
    return [...s].sort();
  }, [aggregatesQ.data]);

  const truncated = (curveQ.data?.length ?? 0) >= CURVE_CAP;

  return (
    <div style={{ padding: "8px 12px" }}>
      {/* ── Account picker row ──────────────────────────────────────────── */}
      <div className="row wrap" style={{ marginBottom: 8, gap: 8 }}>
        <AccountPicker accounts={list} selectedId={selectedId} onSelect={setSelectedId} showAll />
      </div>

      {/* ── Journal filter controls ─────────────────────────────────────── */}
      <div className="row wrap" style={{ marginBottom: 8, gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13 }}>Trade Journal</h3>
        <div className="spacer" />
        <select
          value={preset}
          onChange={(e) => setPreset(e.target.value as RangePreset)}
          style={{ width: "auto", fontSize: 11, padding: "2px 6px" }}
        >
          <option value="deploy">since deploy</option>
          <option value="30d">last 30d</option>
          <option value="7d">last 7d</option>
        </select>
      </div>

      {truncated && (
        <div className="info-banner">
          Equity curve truncated to the most recent {CURVE_CAP} trades; the headline + per-sleeve
          totals stay exact (server-computed over the full set).
        </div>
      )}

      <HistoryStatsCard aggregates={aggregatesQ.data} loading={aggregatesQ.isLoading} />

      <EquityCurve series={series} />

      <SleevePerfTable aggregates={aggregatesQ.data} />

      <EdgeVerificationCard linkedAlgoIds={linkedAlgoIds} />

      <div className="card">
        <div className="row wrap">
          <h2 style={{ margin: 0 }}>Trade journal</h2>
          <div className="spacer" />
          <select
            value={algoPreset}
            onChange={(e) => setAlgoPreset(e.target.value)}
            style={{ width: "auto" }}
          >
            <option value="">all sleeves</option>
            {sleeveOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <JournalTable source={undefined} since={since} algoPreset={algoPreset || undefined} accountId={selectedId ?? undefined} />
      </div>
    </div>
  );
}